import React from "react";
import { Modal } from "~/components/Modal";
import { Routes } from "~/routes";
import * as Analytics from "../../analytics";
import { Link } from "~/components/Link";
import { ChevronRight, BarChart3, Grid2x2Check } from "lucide-react";
import { RWA_COIN_ICON } from "~/consts";
import { ImageLoader } from "~/components/ImageLoader";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
} from "~/components/ui/card";
import { cn } from "~/lib/utils";

// // // //

const DEFAULT_CONTENT = {
    title: "Unlock All Metrics",
    body: (
        <span className="bg-linear-to-r from-indigo-300 to-blue-300 bg-clip-text text-transparent text-lg">
            Create a <span className="font-semibold">free</span> account to
            instantly access this content.
        </span>
    ),
    hideFeaturesSection: false,
};

export function LoginRequiredModal(props: {
    show: boolean;
    analytics?: {
        onConfirmed: () => void;
        onClose: () => void;
    };
    allowScroll?: boolean;
    onHide: () => void;
    content?: {
        title: React.ReactNode;
        body?: React.ReactNode;
        hideFeaturesSection?: boolean;
    };
}) {
    const {
        show,
        content,
        onHide,
        allowScroll,
        analytics = {
            onConfirmed: () => Analytics.trackLoginCtaConfirmed(),
            onClose: () => Analytics.trackLoginCtaClosed(),
        },
    } = props;

    const title = content?.title ?? DEFAULT_CONTENT.title;
    const body = content?.body ?? DEFAULT_CONTENT.body;
    const hideFeaturesSection =
        content?.hideFeaturesSection ?? DEFAULT_CONTENT.hideFeaturesSection;

    return (
        <Modal
            show={show}
            allowScroll={allowScroll}
            onHide={() => {
                analytics.onClose();
                onHide();
            }}
        >
            <Card className="w-full max-w-2xl mx-auto rounded-md border border-slate-900 shadow-2xl overflow-hidden bg-linear-to-br from-slate-900 via-slate-800 to-slate-900">
                <div className="relative">
                    {/* Content */}
                    <CardHeader className="space-y-4 text-center pb-2">
                        <div className="mx-auto bg-slate-700/50 w-12 h-12 rounded-full flex items-center justify-center">
                            <ImageLoader
                                src={RWA_COIN_ICON}
                                alt="RWA Logo"
                                className="w-8 h-8 invert brightness-0"
                            />
                        </div>
                        <CardTitle className="text-3xl text-slate-100">
                            {title}
                        </CardTitle>
                        <CardDescription className="text-md font-light text-slate-300">
                            {body}
                        </CardDescription>

                        <CardDescription className="text-md font-light text-slate-300">
                            Join thousands of professionals leveraging RWA.xyz
                            data to understand the tokenized real-world asset
                            market.
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-6 py-6">
                        {/* Updated CTA Button */}
                        <Link
                            href={Routes.login}
                            className="group flex w-full items-center justify-center gap-2 rounded-md bg-slate-100 px-8 py-3 text-lg font-semibold text-slate-900 shadow-lg transition-all duration-300 hover:bg-white hover:scale-[1.02] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-200 focus-visible:ring-offset-2"
                            onClick={() => {
                                analytics.onConfirmed();
                            }}
                        >
                            Create Your Free Account
                            <ChevronRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>

                        {/* Features Grid */}
                        <div
                            className={cn("grid gap-4 sm:grid-cols-2", {
                                hidden: hideFeaturesSection,
                            })}
                        >
                            {[
                                {
                                    icon: Grid2x2Check,
                                    title: "Reference Data",
                                    description:
                                        "Deal-level terms on all tokenized assets",
                                },
                                {
                                    icon: BarChart3,
                                    title: "Market Analytics",
                                    description:
                                        "Transaction-level data across major blockchains",
                                },
                            ].map((feature, index) => (
                                <div
                                    key={index}
                                    className="flex items-start gap-3 rounded-lg bg-slate-800/50 p-4 border border-white/10"
                                >
                                    <feature.icon className="h-6 w-6 text-slate-300" />
                                    <div>
                                        <h4 className="font-medium text-slate-200/90">
                                            {feature.title}
                                        </h4>
                                        <p className="text-sm text-slate-400">
                                            {feature.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>

                    <CardFooter className="flex flex-col items-center gap-2 text-center pb-6">
                        <p className="text-sm font-light text-slate-300/80">
                            No credit card required. Get started in 2 minutes.
                        </p>
                    </CardFooter>
                </div>
            </Card>
        </Modal>
    );
}
